import { normalize } from "normalizr";
import React from "react";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { toastAdd } from "@/mk2/actions";
import { contestsHomeUrl } from "@/mk2/apps/contests/urls";
import { counsellingsHomeUrl, counsellingsTopicUrl } from "@/mk2/apps/counsellings/urls";
import { forumUpdateComplaintsStatistics } from "@/mk2/apps/forum/actions";
import { CONTESTS_TOPIC_ID, COUNSELLING_CATEGORY_ID } from "@/mk2/apps/forum/constants";
import {
    FORUM_DELETE_TOPIC,
    ForumDeleteTopicAction,
    TOPIC_FETCH_TRIGGER,
    TOPIC_SET_AS_FAVORITE_TRIGGER,
    TOPIC_SUBSCRIBE_TRIGGER,
    TOPIC_UNSUBSCRIBE_TRIGGER,
    TopicDeleteNormalizedResponse,
    TopicFetchNormalizedResponse,
    TopicFetchTriggerAction,
    TopicSetAsFavoriteTriggerAction,
    TopicSubscribeTriggerAction,
    TopicUnsubscribeTriggerAction,
    forumDeleteTopicApi,
    topicFetchApi,
    topicSetAsFavoriteApi,
    topicSubscribeApi,
    topicUnsubscribeApi,
} from "@/mk2/apps/forum/containers/Topic/Topic.actions";
import { TopicState } from "@/mk2/apps/forum/containers/Topic/Topic.reducers";
import { getTopicState } from "@/mk2/apps/forum/containers/Topic/Topic.selectors";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import {
    BrandDaysCampaignEntity,
    BrandDaysCampaignSchema,
    ComplaintsStatisticsPerPost,
    CounsellingEntity,
    CounsellingSchema,
    MyAnonymousPostSchema,
    PostEntity,
    PostSchema,
    TopicEntity,
    TopicSchema,
} from "@/mk2/apps/forum/schemas";
import { forumTopicUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Toast } from "@/mk2/components/toasts/Toast";
import {
    pageHeaderAddFeedMenuItem,
    pageHeaderRemoveFeedMenuItem,
} from "@/mk2/containers/PageHeader/PageHeader.action";
import {
    XHRAction,
    XHRGetError,
    XHRPostError,
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { showSuccessToast } from "@/mk2/helpers/toasts";
import { removeEmpty, url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { AppState } from "@/mk2/reducers";
import { checkIfUserIsLoggedIn } from "@/mk2/sagas/auth";
import { UserEntity } from "@/mk2/schemas";
import { findDenormalizedEntityBySlug, getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryAction, redirectInPWA } from "@/mk2/services/browserHistory";
import {
    ACTION_TO_SET_FAVORITE_TOPIC,
    ACTION_TO_SUBSCRIBE_TOPIC,
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_TOPIC,
    EXPLAIN_TOPIC_DELETION,
    FORUM_TOPIC_ALREADY_SUBSCRIBED_TOAST_name,
    FORUM_TOPIC_SUBSCRIBED_TOAST_name,
    FORUM_TOPIC_UNSUBSCRIBED_TOAST_name,
    FORUM_TOPIC_topic_IS_ADDED_TO_FAVORITES,
    FORUM_TOPIC_topic_IS_REMOVED_FROM_FAVORITES,
} from "@/mk/autogenerated/translation";
import assetUrl from "@/mk/common/assetUrl";
import { photoUrl } from "@/mk/photo/photoUrl";
import {
    forum_api_delete_topic_url,
    forum_api_get_topic_url,
    forum_api_topic_set_as_favorite_url,
    forum_api_topic_subscribe_url,
    forum_api_topic_unsubscribe_url,
} from "@/mk/urls/functions";

const logger = getLogger("forum/Topic.sagas");

export interface ApiResponseTopic {
    body: {
        topic: TopicEntity;
        posts: PostEntity[];
        deletedBy: UserEntity;
        hasNext: number;
        hasPrevious: number;
        isSubscribed: boolean;
        brandDaysCampaign: BrandDaysCampaignEntity;
        brandDaysCampaignGreetingText: string;
        counselling: CounsellingEntity;
        counsellingPostsCount: number | null;
        notificationsOn: number[];
        complaintsStatistics: ComplaintsStatisticsPerPost;
        subscribers: number;
        activeBrandDays: { posts: PostEntity[]; campaign: BrandDaysCampaignEntity };
    };
}

function* fetchTopic({
    slug,
    hashtags,
    query,
    after,
    xhr,
    location,
    history,
    routeNavigate,
}: TopicFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(topicFetchApi.request(slug, hashtags, query, after));

    try {
        const response: ApiResponseTopic = yield call(() =>
            xhr.get(
                forum_api_get_topic_url(slug),
                removeEmpty({
                    after,
                    h: hashtags.length ? hashtags.join(",") : undefined,
                    q: query,
                }),
            ),
        );

        const normalizedResponse: TopicFetchNormalizedResponse = normalize(response.body, {
            posts: [PostSchema],
            myAnonymousPosts: [MyAnonymousPostSchema],
            topic: TopicSchema,
            counselling: CounsellingSchema,

            // used is current Topic is a Brand Days Campaign Topic
            brandDaysCampaign: BrandDaysCampaignSchema,

            // used to fetch currently active Brand Days Campaign data
            activeBrandDays: { posts: [PostSchema], campaign: BrandDaysCampaignSchema },
        });

        const topic: TopicEntity = yield select((state: AppState) =>
            findDenormalizedEntityBySlug<TopicEntity>(
                { ...state, entities: { ...state.entities, ...normalizedResponse.entities } },
                TopicSchema,
                slug,
            ),
        );
        if (topic.category.id === COUNSELLING_CATEGORY_ID) {
            if (!location.pathname.startsWith(counsellingsHomeUrl)) {
                const gotoUrl = url(counsellingsTopicUrl, {
                    topicSlug: slug,
                });
                redirectInPWA(routeNavigate, gotoUrl, 301);
                return;
            }
        } else if (topic.id === CONTESTS_TOPIC_ID) {
            if (!location.pathname.startsWith(contestsHomeUrl)) {
                const gotoUrl = contestsHomeUrl;
                redirectInPWA(routeNavigate, gotoUrl, 301);
                return;
            }
        } else {
            if (!location.pathname.startsWith(forumUrl)) {
                const gotoUrl = url(forumTopicUrl, {
                    topicSlug: slug,
                });
                redirectInPWA(routeNavigate, gotoUrl, 301);
                return;
            }
        }

        yield put(forumUpdateComplaintsStatistics(response.body.complaintsStatistics));

        yield put(
            topicFetchApi.success(
                slug,
                hashtags,
                query,
                after,
                response.body.hasNext,
                response.body.hasPrevious,
                normalizedResponse,
                response.body.notificationsOn,
            ),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, history, logger);
        yield put(topicFetchApi.failure(slug, hashtags, query, after, normalizeError(error)));
    }
}

interface ApiSubscribeTopic {
    body: {
        topic: TopicEntity;
        isSubscribed: boolean;
        changed: boolean;
    };
}

function* subscribeTopic({
    slug,
    username,
    xhr,
    location,
    history,
    routeNavigate,
}: TopicSubscribeTriggerAction & XHRAction & HistoryAction) {
    if (!(yield call(checkIfUserIsLoggedIn, routeNavigate, location))) {
        return;
    }

    yield put(topicSubscribeApi.request(slug));

    try {
        const response: ApiSubscribeTopic = yield call(() =>
            xhr.post(forum_api_topic_subscribe_url(username), { slug }),
        );

        const topic: TopicEntity = response.body.topic;
        const changed: boolean = response.body.changed;

        const normalizedResponse: TopicDeleteNormalizedResponse = normalize(
            {
                topic: response.body.topic,
            },
            {
                topic: TopicSchema,
            },
        );

        const msg = changed
            ? FORUM_TOPIC_SUBSCRIBED_TOAST_name
            : FORUM_TOPIC_ALREADY_SUBSCRIBED_TOAST_name;
        yield put(
            toastAdd("success", {
                component: ({ remove }) => (
                    <Toast type="success" remove={remove}>
                        <Interpolate i18nKey={msg} name={<strong>{topic.name}</strong>} />
                    </Toast>
                ),
            }),
        );

        yield put(topicSubscribeApi.success(slug, response.body.isSubscribed));

        const icon = topic.coverPhoto
            ? photoUrl(topic.coverPhoto, "s96x96")
            : assetUrl + "img/photoblog/groups/icon-feed-menu-group.gif";

        yield put(
            pageHeaderAddFeedMenuItem(
                "my-forums",
                `topic-${topic.id}`,
                topic.name,
                icon,
                topicUrl(topic),
            ),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(topicSubscribeApi.failure(slug, normalizeError(error)));
    }
}

interface ApiUnsubscribeTopic {
    body: {
        isSubscribed: boolean;
    };
}

function* unsubscribeTopic({
    slug,
    username,
    xhr,
    history,
    routeNavigate,
}: TopicUnsubscribeTriggerAction & XHRAction & HistoryAction) {
    yield put(topicUnsubscribeApi.request(slug));

    try {
        const response: ApiUnsubscribeTopic = yield call(() =>
            xhr.post(forum_api_topic_unsubscribe_url(username), { slug }),
        );

        const topic: TopicEntity = yield select((state: AppState) =>
            findDenormalizedEntityBySlug<TopicEntity>(state, TopicSchema, slug),
        );

        yield put(topicUnsubscribeApi.success(slug, response.body.isSubscribed));

        yield put(pageHeaderRemoveFeedMenuItem("my-forums", `topic-${topic.id}`));
        const topicState: TopicState = yield select((state: AppState) =>
            getTopicState(state, topic.slug),
        );
        if (topicState.isFavorite) {
            yield put(pageHeaderRemoveFeedMenuItem("my-favorites", `topic-${topic.id}`));
        }

        yield put(
            toastAdd("success", {
                component: ({ remove }) => (
                    <Toast type="success" remove={remove}>
                        <Interpolate
                            i18nKey={FORUM_TOPIC_UNSUBSCRIBED_TOAST_name}
                            name={<strong>{topic.name}</strong>}
                        />
                    </Toast>
                ),
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(topicUnsubscribeApi.failure(slug, normalizeError(error)));
    }
}

interface ApiSetTopicAsFavorite {
    body: {
        isFavorite: boolean;
    };
}

function* setTopicAsFavorite({
    topicId,
    userId,
    setAsFavorite,
    xhr,
    location,
    history,
    routeNavigate,
}: TopicSetAsFavoriteTriggerAction & XHRAction & HistoryAction) {
    const topic: TopicEntity = yield select((state: AppState) =>
        getDenormalizedEntity<TopicEntity>(state, TopicSchema, topicId),
    );

    if (!(yield call(checkIfUserIsLoggedIn, routeNavigate, location))) {
        return;
    }

    yield put(topicSetAsFavoriteApi.request(topic.slug, userId, setAsFavorite));

    try {
        const response: ApiSetTopicAsFavorite = yield call(() =>
            xhr.post(forum_api_topic_set_as_favorite_url(), {
                topicId,
                userId,
                setAsFavorite,
            }),
        );

        yield put(topicSetAsFavoriteApi.success(topic.slug, userId, setAsFavorite));

        const icon = topic.coverPhoto
            ? photoUrl(topic.coverPhoto, "s96x96")
            : assetUrl + "img/photoblog/groups/icon-feed-menu-group.gif";

        if (setAsFavorite) {
            yield put(
                pageHeaderAddFeedMenuItem(
                    "my-favorites",
                    `topic-${topic.id}`,
                    topic.name,
                    icon,
                    topicUrl(topic),
                ),
            );
            yield put(pageHeaderRemoveFeedMenuItem("my-forums", `topic-${topic.id}`));
        } else {
            yield put(pageHeaderRemoveFeedMenuItem("my-favorites", `topic-${topic.id}`));
            yield put(
                pageHeaderAddFeedMenuItem(
                    "my-forums",
                    `topic-${topic.id}`,
                    topic.name,
                    icon,
                    topicUrl(topic),
                ),
            );
        }

        yield showSuccessToast(
            <Interpolate
                i18nKey={
                    response.body.isFavorite
                        ? FORUM_TOPIC_topic_IS_ADDED_TO_FAVORITES
                        : FORUM_TOPIC_topic_IS_REMOVED_FROM_FAVORITES
                }
                group={<strong>{topic.name}</strong>}
            />,
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(
            topicSetAsFavoriteApi.failure(topic.slug, userId, setAsFavorite, normalizeError(error)),
        );
    }
}

interface ApiResponseDeleteTopic {
    body: {
        topic: TopicEntity;
        isSubscribed: boolean;
    };
}

function* deleteTopic({ slug, xhr, history }: ForumDeleteTopicAction & XHRAction & HistoryAction) {
    let reason;
    if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_TOPIC)) {
        return;
    } else {
        reason = window.prompt(EXPLAIN_TOPIC_DELETION);
        if (reason === null) {
            return;
        }
    }

    yield put(forumDeleteTopicApi.request(slug));

    try {
        const response: ApiResponseDeleteTopic = yield call(() =>
            xhr.post(forum_api_delete_topic_url(slug), { comment: reason }),
        );

        const normalizedResponse: TopicDeleteNormalizedResponse = normalize(
            {
                topic: response.body.topic,
            },
            {
                topic: TopicSchema,
            },
        );

        yield put(forumDeleteTopicApi.success(slug, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(forumDeleteTopicApi.failure(slug, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(TOPIC_FETCH_TRIGGER, fetchTopic),
        takeEvery(TOPIC_SUBSCRIBE_TRIGGER, subscribeTopic),
        takeEvery(TOPIC_UNSUBSCRIBE_TRIGGER, unsubscribeTopic),
        takeEvery(TOPIC_SET_AS_FAVORITE_TRIGGER, setTopicAsFavorite),
        takeEvery(FORUM_DELETE_TOPIC, deleteTopic),
    ]);
}
